/**
 * Bulk-import spec for the `Sessions` sheet of the organizer workbook
 * (ADR-120 Option B: single-cell audience).
 *
 * Sessions reference four other entity types — categories, locations,
 * audiences, and people (speakers) — by label/name/email. The admin spec
 * validates format only; the bulk callable does all the per-event
 * lookups in one round-trip.
 *
 * The `audience` column is one cell per row. The dialog passes it
 * verbatim to the callable, which detects the cell shape:
 *   - contains '@'              → personal audience by email match
 *   - contains ',' (no '@')     → comma-split names, anonymous composition
 *                                  union of the named audiences
 *   - otherwise (non-empty)     → named audience by name match
 *   - empty                     → use the row's category defaultAudienceId
 *
 * Anonymous compositions (multi-audience targeting) are exercised by the
 * seed when ingesting workbook rows. The admin bulk-import dialog falls
 * back to single-name lookup for the comma-list case (v2.1 follow-up:
 * extend bulkResolveSessions/bulkCreateSessions to mirror this).
 *
 * Time fields are parsed in event-local YYYY-MM-DD HH:MM format and kept
 * as a structured pair of strings here. The dialog converts them to UTC
 * Timestamps using `dateTimeLocalToTimestamp(localString, event.timezone)`
 * before sending to the callable. The dialog has the event timezone via
 * `useEvent`; the spec stays portable across events.
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { EMAIL_PATTERN, normalize } from "./importTypes";

export interface RawSessionImportRow extends Record<string, string | undefined> {
  title?: string;
  description?: string;
  sessionTypeName?: string;
  startTime?: string;
  endTime?: string;
  locationLabel?: string;
  audience?: string;
  speakerEmails?: string;
}

export interface ParsedSessionImportRow {
  title: string;
  description: string | null;
  sessionTypeName: string;
  /** Event-local naive datetime: "YYYY-MM-DDTHH:mm". Dialog converts to UTC. */
  startTimeLocal: string;
  endTimeLocal: string;
  locationLabel: string | null;
  /** Single audience cell — name or email. Null = use category default. */
  audience: string | null;
  speakerEmails: string[];
}

/** Match "YYYY-MM-DD HH:MM" or "YYYY-MM-DDTHH:MM" (with optional seconds). */
const LOCAL_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;

function parseLocalDateTime(raw: string | undefined): string | null {
  const value = normalize(raw);
  if (!value) return null;
  const m = LOCAL_DATETIME_PATTERN.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function splitCommaList(raw: string | undefined): string[] {
  return normalize(raw)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const sessionImportSpec: ImportSpec<
  RawSessionImportRow,
  ParsedSessionImportRow
> = {
  sheetName: "Sessions",
  purpose:
    "Agenda items — talks, meals, breakouts, dinners, free time. Anything on the schedule.",
  columns: [
    { key: "title", note: "required" },
    {
      key: "description",
      note: "optional — appears on the session detail screen",
    },
    {
      key: "sessionTypeName",
      note: "must match one of the event's Categories (Presentation, Meal, Activity, etc.)",
    },
    { key: "startTime", note: "YYYY-MM-DD HH:MM in event-local time" },
    { key: "endTime", note: "YYYY-MM-DD HH:MM in event-local time" },
    {
      key: "locationLabel",
      note: "optional — the Location label where this session happens",
    },
    {
      key: "audience",
      note:
        "either a group name (e.g. 'All Members', 'Skiers'), one attendee email, OR a comma-separated list of group names for multi-audience targeting (creates an anonymous composition union, e.g. 'Skiers, Snowboarders'). Leave blank to use the category's default audience.",
    },
    {
      key: "speakerEmails",
      note: "comma-separated emails of users who present this session (must already be Users)",
    },
  ],
  example: {
    title: "Dinner at Mario's",
    description:
      "Private dinner with investors. Shuttle from the hotel at 6:45pm.",
    sessionTypeName: "Dinner",
    startTime: "2026-02-13 19:00",
    endTime: "2026-02-13 22:00",
    locationLabel: "Mario's Trattoria",
    audience: "Dinner — Friday Mario's",
    speakerEmails: "",
  },
  identity: {
    idPrefix: "session",
    label: (row) => row.title,
    naturalKey: (row) => [
      row.title,
      row.startTimeLocal,
      row.locationLabel,
    ],
  },
  validate(raw, lineNumber) {
    const title = normalize(raw.title);
    const description = normalize(raw.description) || null;
    const sessionTypeName = normalize(raw.sessionTypeName);
    const startTimeLocal = parseLocalDateTime(raw.startTime);
    const endTimeLocal = parseLocalDateTime(raw.endTime);
    const locationLabel = normalize(raw.locationLabel) || null;
    const audience = normalize(raw.audience) || null;
    const speakerEmails = splitCommaList(raw.speakerEmails).map((e) =>
      e.toLowerCase(),
    );

    const base: Omit<ParsedRow<ParsedSessionImportRow>, "status" | "message"> =
      {
        lineNumber,
        data: {
          title,
          description,
          sessionTypeName,
          startTimeLocal: startTimeLocal ?? "",
          endTimeLocal: endTimeLocal ?? "",
          locationLabel,
          audience,
          speakerEmails,
        },
      };

    if (!title) return invalid(base, "Title is required");
    if (!sessionTypeName) {
      return invalid(base, "sessionTypeName is required");
    }
    if (!startTimeLocal) {
      return invalid(
        base,
        'startTime must be in "YYYY-MM-DD HH:MM" format (event-local time).',
      );
    }
    if (!endTimeLocal) {
      return invalid(
        base,
        'endTime must be in "YYYY-MM-DD HH:MM" format (event-local time).',
      );
    }
    if (endTimeLocal <= startTimeLocal) {
      return invalid(base, "endTime must be after startTime.");
    }

    // Format-validate the audience cell only when it looks like an
    // email. Names go straight through — the server confirms the match.
    if (audience && audience.includes("@") && !EMAIL_PATTERN.test(audience)) {
      return invalid(base, `Invalid audience email: "${audience}"`);
    }

    for (const email of speakerEmails) {
      if (!EMAIL_PATTERN.test(email)) {
        return invalid(base, `Invalid speaker email: "${email}"`);
      }
    }

    // Lowercase only emails (case-insensitive). Names preserve original
    // casing for error messages — server matches case-insensitively.
    return pending({
      ...base,
      data: {
        ...base.data,
        audience:
          audience && audience.includes("@") ? audience.toLowerCase() : audience,
        speakerEmails: Array.from(new Set(speakerEmails)),
      },
    });
  },
  // No within-batch dedup: the same session title can legitimately appear
  // on different days. Server-side dup check uses (title, startTime).
};
